import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useCustomer, type Purchase } from "../customer/CustomerContext";
import { FAVORITE_CATALOG } from "../customer/catalog";

export function AccountPage() {
  const {
    customer,
    loading,
    language,
    currency,
    favorites,
    setLanguage,
    setCurrency,
    logout,
    refreshPurchases,
    updateProfile,
  } = useCustomer();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setFirstName(customer.first_name || "");
    setLastName(customer.last_name || "");
    void refreshPurchases()
      .then(setPurchases)
      .catch(() => setPurchases([]));
  }, [customer, refreshPurchases]);

  if (loading) {
    return (
      <article className="page account-page">
        <p className="page-summary">Loading your account…</p>
      </article>
    );
  }

  if (!customer) return <Navigate to="/account/login" replace />;

  async function onSaveProfile(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      await updateProfile({ first_name: firstName.trim(), last_name: lastName.trim() });
      setMsg("Profile saved.");
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  }

  const favItems = FAVORITE_CATALOG.filter((item) => favorites.includes(item.id));

  return (
    <article className="page account-page">
      <p className="eyebrow">Customer account</p>
      <h1>
        Hello{customer.first_name ? `, ${customer.first_name}` : ""}
      </h1>
      <p className="page-summary">
        Manage your profile, purchases, favorites, language, and currency.
      </p>

      <section className="account-section">
        <h2>Profile</h2>
        <form className="deposit-form account-form" onSubmit={onSaveProfile}>
          <div className="buy-server-row">
            <label htmlFor="acct-email">Email</label>
            <div className="buy-server-control">
              <input id="acct-email" type="email" value={customer.email} disabled readOnly />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="acct-first">First name</label>
            <div className="buy-server-control">
              <input
                id="acct-first"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="acct-last">Last name</label>
            <div className="buy-server-control">
              <input
                id="acct-last"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="buy-server-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save profile"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => void logout()}
            >
              Log out
            </button>
          </div>
          {msg ? <p className="account-ok">{msg}</p> : null}
          {err ? (
            <p className="model-run-error" role="alert">
              {err}
            </p>
          ) : null}
        </form>
      </section>

      <section className="account-section">
        <h2>Preferences</h2>
        <div className="account-pref-row">
          <label htmlFor="acct-lang">Language</label>
          <select
            id="acct-lang"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
          </select>
        </div>
        <div className="account-pref-row">
          <label htmlFor="acct-cur">Currency</label>
          <select
            id="acct-cur"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </section>

      <section className="account-section">
        <div className="account-section-head">
          <h2>Favorites</h2>
          <Link to="/account/favorites">Manage</Link>
        </div>
        {favItems.length === 0 ? (
          <p className="account-muted">No favorites yet. Heart a service to save it here.</p>
        ) : (
          <ul className="account-list">
            {favItems.map((item) => (
              <li key={item.id}>
                <Link to={item.href}>{item.label}</Link>
                <span>{item.note}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="account-section">
        <h2>Purchases &amp; trials</h2>
        {purchases.length === 0 ? (
          <p className="account-muted">
            No purchases yet. Orders and trials placed with this email will show here.
          </p>
        ) : (
          <ul className="account-list">
            {purchases.map((p) => (
              <li key={`${p.kind}-${p.id}`}>
                <strong>{p.label}</strong>
                <span>
                  {p.status}
                  {p.amount != null ? ` · ${p.currency || currency} ${p.amount}` : ""}
                  {p.company_name ? ` · ${p.company_name}` : ""}
                  {p.created_at ? ` · ${new Date(p.created_at).toLocaleString()}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}
